import { collection, doc, addDoc, updateDoc, getDoc, query, where, getDocs, arrayUnion } from 'firebase/firestore'
import { db } from './config'
import { GuestUser } from '../../types'

/**
 * Create or get a guest user
 */
export const createGuestUser = async (username: string): Promise<string> => {
  try {
    // Check if guest with this username already exists (optional)
    const guestData = {
      username,
      createdAt: new Date().toISOString(),
      projectsViewed: []
    }

    const docRef = await addDoc(collection(db, 'guests'), guestData)
    return docRef.id
  } catch (error) {
    console.error('Failed to create guest user:', error)
    throw error
  }
}

/**
 * Record that a guest viewed a project
 */
export const recordGuestView = async (guestId: string, projectId: string): Promise<void> => {
  try {
    const guestRef = doc(db, 'guests', guestId)
    await updateDoc(guestRef, {
      projectsViewed: arrayUnion(projectId)
    })
  } catch (error) {
    console.error('Failed to record guest view:', error)
    throw error
  }
}

/**
 * Get guest user by ID
 */
export const getGuestUser = async (guestId: string): Promise<GuestUser | null> => {
  try {
    const guestRef = doc(db, 'guests', guestId)
    const guestSnap = await getDoc(guestRef)
    
    if (guestSnap.exists()) {
      return {
        id: guestSnap.id,
        ...guestSnap.data()
      } as GuestUser
    }
    
    return null
  } catch (error) {
    console.error('Failed to get guest user:', error)
    return null
  }
}

/**
 * Save guest comment to an image
 */
export const saveGuestComment = async (
  imageId: string,
  guestUsername: string,
  content: string
): Promise<void> => {
  try {
    const imageRef = doc(db, 'images', imageId)
    const imageSnap = await getDoc(imageRef)
    
    if (!imageSnap.exists()) {
      throw new Error('Image not found')
    }

    const newComment = {
      id: Date.now().toString(),
      imageId,
      userId: `guest_${Date.now()}`,
      userEmail: '',
      username: guestUsername,
      isGuest: true,
      content,
      createdAt: new Date().toISOString(),
      replies: [],
      likes: 0
    }

    const currentComments = imageSnap.data().comments || []
    await updateDoc(imageRef, {
      comments: [...currentComments, newComment]
    })
  } catch (error) {
    console.error('Failed to save guest comment:', error)
    throw error
  }
}

/**
 * Toggle like on an image by guest
 */
export const toggleGuestLike = async (
  imageId: string,
  guestUsername: string
): Promise<boolean> => {
  try {
    const imageRef = doc(db, 'images', imageId)
    const imageSnap = await getDoc(imageRef)
    
    if (!imageSnap.exists()) {
      throw new Error('Image not found')
    }

    const imageData = imageSnap.data()
    const likedBy = imageData.likedBy || []
    const isLiked = likedBy.includes(guestUsername)
    
    if (isLiked) {
      // Unlike
      await updateDoc(imageRef, {
        likes: Math.max(0, (imageData.likes || 0) - 1),
        likedBy: likedBy.filter((name: string) => name !== guestUsername)
      })
      return false
    } else {
      // Like
      await updateDoc(imageRef, {
        likes: (imageData.likes || 0) + 1,
        likedBy: [...likedBy, guestUsername]
      })
      return true
    }
  } catch (error) {
    console.error('Failed to toggle guest like:', error)
    throw error
  }
}

